import { useMemo } from 'react';

import { useAuth } from '@app/auth';

// Jerarquía de roles, espejo del backend (`middleware/auth.py`).
// `coordinador` se mantiene mapeado a admin como alias deprecado mientras
// existan filas con ese rol en BD; ya no aparece en formularios ni en
// matrices nuevas — usar `isSuperAdmin` / `isAdmin` / `isAgent`.
const SUPERADMIN_ROLES = new Set(['super admin', 'superadmin']);
const ADMIN_ROLES = new Set([...SUPERADMIN_ROLES, 'admin', 'administrador', 'coordinador']);
const AGENT_ROLES = new Set([...ADMIN_ROLES, 'agente', 'asistente']);

function normalize(role: string | undefined | null): string {
  return (role ?? '').trim().toLowerCase();
}

function isOnlyAgent(role: string): boolean {
  return role === 'agente' || role === 'asistente';
}

/**
 * Hook para consultar permisos de forma declarativa. Sigue la misma matriz
 * que el backend (`docs/roles.md` en `italyca-backend`).
 *
 * - `isSuperAdmin`: ve dashboard, permisos ilimitados.
 * - `isAdmin`: todo salvo dashboard.
 * - `isAgent`: cualquiera de los roles autenticados (sirve como check base).
 * - `isAgentOnly`: SOLO agente/asistente — útil para ocultar botones de
 *   creación en módulos restringidos y filtrar listados.
 */
export function usePermissions() {
  const { user } = useAuth();
  const role = normalize(user?.role);

  return useMemo(
    () => ({
      role,
      isSuperAdmin: SUPERADMIN_ROLES.has(role),
      isAdmin: ADMIN_ROLES.has(role),
      isAgent: AGENT_ROLES.has(role),
      isAgentOnly: isOnlyAgent(role),
      can: (needed: 'superadmin' | 'admin' | 'agent'): boolean => {
        if (needed === 'superadmin') return SUPERADMIN_ROLES.has(role);
        if (needed === 'admin') return ADMIN_ROLES.has(role);
        return AGENT_ROLES.has(role);
      },
    }),
    [role]
  );
}

export default usePermissions;
